import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  ChartLineUp,
  Cursor,
  Eye,
  Target,
  Trophy,
  Warning,
  Sparkle,
  ArrowsClockwise,
  Lightning,
  Robot,
  ArrowRight,
  GoogleLogo,
  ChartBar,
  PlugsConnected,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import SeoReportTab from '../components/SeoReportTab';
import GoogleIntegrationsPage from './GoogleIntegrationsPage';

const API = process.env.REACT_APP_BACKEND_URL;
const STALE_AFTER_SECONDS = 86400; // 24h

export default function SeoInsightsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');
  const [properties, setProperties] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Most users only want product/category opportunities — Sortilize cannot
  // auto-optimise blog posts. Hide them by default; user can re-enable.
  const [hidePosts, setHidePosts] = useState(true);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const loadCached = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/insights/summary`, { headers: getHeaders() });
      if (d.cached) {
        setData(d);
        setSiteUrl(d.site_url);
        return d;
      }
    } catch (err) {
      // ignore — empty cache
    } finally {
      setLoading(false);
    }
    return null;
  }, [getHeaders]);

  const loadProps = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/google/gsc/properties`, { headers: getHeaders() });
      const verified = (d.properties || []).filter((p) => p.verified);
      setProperties(verified);
      if (!siteUrl && verified.length) setSiteUrl(verified[0].site_url);
    } catch (err) {
      // google not connected — handled by redirect
    }
  }, [getHeaders, siteUrl]);

  useEffect(() => {
    (async () => {
      const cached = await loadCached();
      await loadProps();
      // Auto-refresh if no cache OR stale (>24h)
      if (!cached) return;
      if (cached.stale || (cached.cache_age_seconds || 0) > STALE_AFTER_SECONDS) {
        // Run refresh in background; user sees stale data immediately
        try {
          setRefreshing(true);
          const { data: fresh } = await axios.post(
            `${API}/api/insights/refresh`,
            { site_url: cached.site_url, days: 30 },
            { headers: getHeaders() },
          );
          setData(fresh);
        } catch {
          // silent — stale cache still shown
        } finally {
          setRefreshing(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    if (!siteUrl) { toast.error('Velg en GSC property først'); return; }
    setRefreshing(true);
    try {
      const { data: d } = await axios.post(
        `${API}/api/insights/refresh`,
        { site_url: siteUrl, days: 30 },
        { headers: getHeaders() },
      );
      setData(d);
      toast.success('SEO-data oppdatert fra Google!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke hente data');
    } finally { setRefreshing(false); }
  };

  const toggleSelect = (productId) => {
    if (!productId) return;
    const next = new Set(selectedIds);
    if (next.has(productId)) next.delete(productId); else next.add(productId);
    setSelectedIds(next);
  };

  const selectAllInBucket = (rows) => {
    const ids = (rows || []).filter((r) => r.product_id).map((r) => r.product_id);
    if (!ids.length) {
      toast.info('Ingen produkter å velge i denne fanen — kun blogginnlegg/ukoblede sider.');
      return;
    }
    const allSelected = ids.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSelected) {
      ids.forEach((id) => next.delete(id));
    } else {
      ids.forEach((id) => next.add(id));
    }
    setSelectedIds(next);
  };

  const sendToAutoGuard = async (reason) => {
    if (selectedIds.size === 0) { toast.error('Velg minst ett produkt'); return; }
    try {
      const { data: r } = await axios.post(
        `${API}/api/insights/send-to-autoguard`,
        { product_ids: Array.from(selectedIds), reason },
        { headers: getHeaders() },
      );
      toast.success(`${r.updated} produkter satt i prioritert re-optimaliseringskø i AutoGuard`);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error('Kunne ikke sende til AutoGuard');
    }
  };

  if (loading) {
    return <div className="space-y-4">
      <div className="h-8 w-64 bg-muted animate-pulse rounded" />
      <div className="h-64 bg-muted animate-pulse rounded-md" />
    </div>;
  }

  const noConnection = !data && properties.length === 0;
  const filtBucket = (rows) => hidePosts ? (rows || []).filter((r) => !r.is_post) : (rows || []);

  return (
    <div className="space-y-4" data-testid="insights-page">
      {/* Header + refresh */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl tracking-tight font-extrabold flex items-center gap-3">
            <ChartLineUp size={36} weight="bold" /> SEO
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Rapport, detaljer og Google-tilkobling — alt på ett sted.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {data?.generated_at && (
            <span className="text-xs text-muted-foreground">
              {refreshing ? 'Henter...' : 'Oppdatert'} {new Date(data.generated_at).toLocaleString('nb-NO')}
            </span>
          )}
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="hide-posts-toggle">
            <Checkbox checked={hidePosts} onCheckedChange={(v) => setHidePosts(!!v)} />
            Skjul blogginnlegg
          </label>
          <Button onClick={refresh} disabled={refreshing || !siteUrl} className="gap-2" data-testid="refresh-insights-btn">
            <ArrowsClockwise size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Henter...' : 'Oppdater data'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue={noConnection ? 'connection' : 'report'} className="space-y-4">
        <TabsList>
          <TabsTrigger value="report" className="gap-1" data-testid="tab-report">
            <ChartBar size={14} /> Rapport
          </TabsTrigger>
          <TabsTrigger value="details" className="gap-1" data-testid="tab-details">
            <Target size={14} /> Detaljer
          </TabsTrigger>
          <TabsTrigger value="connection" className="gap-1" data-testid="tab-connection">
            <PlugsConnected size={14} /> Tilkobling
          </TabsTrigger>
        </TabsList>

        {/* TAB 1 — Rapport */}
        <TabsContent value="report">
          {!data ? (
            <Card className="border-2 border-primary/30">
              <CardContent className="p-8 text-center">
                <ChartLineUp size={40} className="mx-auto mb-3 text-primary" />
                <h2 className="font-heading text-xl font-bold mb-2">Ingen SEO-data ennå</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {properties.length === 0
                    ? 'Koble til Google Search Console under fanen «Tilkobling».'
                    : 'Trykk «Oppdater data» øverst for å hente fra Google.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <SeoReportTab
              data={data}
              hidePosts={hidePosts}
              navigate={navigate}
              onSendToAutoGuard={(idsOrAction) => {
                if (Array.isArray(idsOrAction)) {
                  setSelectedIds(new Set(idsOrAction));
                  setTimeout(() => sendToAutoGuard('SEO Rapport: Topp muligheter'), 100);
                }
              }}
            />
          )}
        </TabsContent>

        {/* TAB 2 — Detaljer (existing buckets) */}
        <TabsContent value="details">
          {!data ? (
            <Card className="border border-border">
              <CardContent className="p-12 text-center">
                <Sparkle size={40} className="mx-auto mb-3 text-primary animate-pulse" />
                <h2 className="font-heading text-xl font-bold mb-2">Klar til første analyse</h2>
                <p className="text-sm text-muted-foreground">
                  Trykk "Oppdater data" over for å hente SEO-data fra Google Search Console.
                  <br />Første gang tar ca. 10–20 sekunder.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Overview stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <StatCard label="Totale klikk" value={data.totals?.clicks?.toLocaleString('nb-NO')} icon={Cursor} />
                <StatCard label="Totale visninger" value={data.totals?.impressions?.toLocaleString('nb-NO')} icon={Eye} />
                <StatCard label="Snitt CTR" value={`${(data.totals?.ctr * 100).toFixed(2)}%`} icon={Target} />
                <StatCard label="Sider m/trafikk" value={data.totals?.total_pages} icon={ChartLineUp} />
              </div>

              {/* Action hint */}
              {selectedIds.size > 0 && (
                <Card className="border-2 border-primary bg-primary/5 sticky top-2 z-10 mb-4">
                  <CardContent className="p-3 flex items-center justify-between flex-wrap gap-3">
                    <span className="text-sm font-medium">{selectedIds.size} produkter valgt</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())}>Nullstill</Button>
                      <Button size="sm" onClick={() => sendToAutoGuard('SEO Insights: Re-optimaliser CTR')} className="gap-1" data-testid="send-to-autoguard-btn">
                        <Robot size={14} /> Re-optimaliser ({selectedIds.size})
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Tabs defaultValue="low_ctr" className="space-y-4">
                <TabsList className="flex-wrap h-auto">
                  <TabsTrigger value="low_ctr" className="gap-1">
                    <Warning size={14} weight="bold" className="text-destructive" />
                    Lav CTR <Badge variant="secondary" className="ml-1">{filtBucket(data.quick_wins?.low_ctr).length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="striking" className="gap-1">
                    <Target size={14} weight="bold" className="text-warning" />
                    Nesten topp <Badge variant="secondary" className="ml-1">{filtBucket(data.quick_wins?.striking_distance).length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="goldmines" className="gap-1">
                    <Sparkle size={14} weight="bold" className="text-primary" />
                    Gullklumper <Badge variant="secondary" className="ml-1">{data.quick_wins?.query_goldmines?.length || 0}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="winners" className="gap-1">
                    <Trophy size={14} weight="bold" className="text-success" />
                    Vinnere <Badge variant="secondary" className="ml-1">{filtBucket(data.quick_wins?.winners).length}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="zero" className="gap-1">
                    <Warning size={14} weight="bold" />
                    0 klikk <Badge variant="secondary" className="ml-1">{filtBucket(data.quick_wins?.zero_clicks).length}</Badge>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="low_ctr">
                  <BucketCard
                    title="🔴 Høy visning, lav CTR — Din største gevinst-mulighet"
                    description="Google viser disse sidene ofte, men folk klikker ikke. Bedre tittel/meta = mer klikk uten nytt innhold."
                    rows={filtBucket(data.quick_wins?.low_ctr)}
                    showPriorityScore
                    showSelectionForProducts
                    selectedIds={selectedIds}
                    onToggle={toggleSelect}
                    onSelectAll={selectAllInBucket}
                  />
                </TabsContent>

                <TabsContent value="striking">
                  <BucketCard
                    title="🟡 Striking Distance — Nesten på topp"
                    description="Disse rangerer 4-15. En liten SEO-push kan flytte dem til topp 3."
                    rows={filtBucket(data.quick_wins?.striking_distance)}
                    showSelectionForProducts
                    selectedIds={selectedIds}
                    onToggle={toggleSelect}
                    onSelectAll={selectAllInBucket}
                  />
                </TabsContent>

                <TabsContent value="goldmines">
                  <GoldmineCard rows={data.quick_wins?.query_goldmines || []} />
                </TabsContent>

                <TabsContent value="winners">
                  <BucketCard
                    title="🟢 Vinnere — IKKE rør disse"
                    description="Disse sidene fungerer allerede. AutoGuard er satt til å ikke endre tittel/meta på vinnere."
                    rows={filtBucket(data.quick_wins?.winners)}
                    disableSelection
                  />
                </TabsContent>

                <TabsContent value="zero">
                  <BucketCard
                    title="❌ 0 klikk — Tittel eller intent trigger ikke interesse"
                    description="Disse vises mye i Google men ingen klikker. Sjekk om tittel matcher søket, eller om siden rangerer på feil søkeord."
                    rows={filtBucket(data.quick_wins?.zero_clicks)}
                    showSelectionForProducts
                    selectedIds={selectedIds}
                    onToggle={toggleSelect}
                    onSelectAll={selectAllInBucket}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </TabsContent>

        {/* TAB 3 — Tilkobling */}
        <TabsContent value="connection">
          <GoogleIntegrationsPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="border border-border">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-md">
          <Icon size={20} weight="bold" className="text-primary" />
        </div>
        <div>
          <p className="font-heading text-2xl font-extrabold">{value ?? '—'}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}


function BucketCard({ title, description, rows, showPriorityScore, showSelectionForProducts, disableSelection, selectedIds, onToggle, onSelectAll }) {
  if (!rows?.length) {
    return (
      <Card className="border border-border">
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          Ingen treff i denne kategorien.
        </CardContent>
      </Card>
    );
  }
  const productCount = rows.filter((r) => r.product_id).length;
  const postCount = rows.filter((r) => r.is_post).length;
  const selectableSelected = rows.filter((r) => r.product_id && selectedIds?.has(r.product_id)).length;
  const allSelected = productCount > 0 && selectableSelected === productCount;
  return (
    <Card className="border border-border">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="font-heading text-lg">{title}</CardTitle>
            <p className="text-xs text-muted-foreground">{description}</p>
            {(productCount > 0 || postCount > 0) && (
              <p className="text-xs text-muted-foreground mt-1">
                {productCount > 0 && <span>{productCount} produkter</span>}
                {productCount > 0 && postCount > 0 && <span> · </span>}
                {postCount > 0 && <span>{postCount} blogginnlegg</span>}
              </p>
            )}
          </div>
          {showSelectionForProducts && !disableSelection && productCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSelectAll?.(rows)}
              data-testid="select-all-btn"
              className="gap-1"
            >
              <Checkbox checked={allSelected} className="pointer-events-none" />
              {allSelected ? 'Fjern alle' : `Velg alle (${productCount})`}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                {showSelectionForProducts && !disableSelection && <th className="w-10 p-3"></th>}
                <th className="text-left p-3 font-semibold">Side / Produkt / Innlegg</th>
                <th className="text-right p-3 font-semibold">Klikk</th>
                <th className="text-right p-3 font-semibold">Visn.</th>
                <th className="text-right p-3 font-semibold">CTR</th>
                <th className="text-right p-3 font-semibold">Pos.</th>
                {showPriorityScore && <th className="text-right p-3 font-semibold">Potensial</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border hover:bg-muted/20">
                  {showSelectionForProducts && !disableSelection && (
                    <td className="p-3">
                      {r.product_id ? (
                        <Checkbox
                          checked={selectedIds?.has(r.product_id) || false}
                          onCheckedChange={() => onToggle(r.product_id)}
                          data-testid={`select-insight-${r.product_id}`}
                        />
                      ) : (
                        <span className="text-muted-foreground text-xs">–</span>
                      )}
                    </td>
                  )}
                  <td className="p-3 max-w-[500px]">
                    {r.product_name ? (
                      <Link to={`/products/${r.product_id}`} className="block hover:text-primary">
                        <span className="font-medium truncate block">{r.product_name}</span>
                        <span className="text-xs text-muted-foreground truncate block">{r.page?.replace(/^https?:\/\/[^/]+/, '')}</span>
                      </Link>
                    ) : r.is_post ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">📝 Innlegg</Badge>
                          <span className="font-medium truncate" dangerouslySetInnerHTML={{ __html: r.post_title || '' }} />
                        </div>
                        <span className="text-xs text-muted-foreground truncate block">{r.page?.replace(/^https?:\/\/[^/]+/, '')}</span>
                        {r.post_edit_url && (
                          <a
                            href={r.post_edit_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                            data-testid={`open-wp-${r.post_id}`}
                          >
                            Åpne i WordPress <ArrowRight size={12} />
                          </a>
                        )}
                      </div>
                    ) : (
                      <a href={r.page} target="_blank" rel="noreferrer" className="block hover:text-primary">
                        <span className="truncate block">{r.page?.replace(/^https?:\/\/[^/]+/, '')}</span>
                      </a>
                    )}
                    {r.reason && <p className="text-xs text-muted-foreground italic mt-1">{r.reason}</p>}
                  </td>
                  <td className="p-3 text-right font-mono">{r.clicks}</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{r.impressions?.toLocaleString('nb-NO')}</td>
                  <td className={`p-3 text-right font-mono ${r.ctr < 0.02 ? 'text-destructive' : r.ctr > 0.05 ? 'text-success' : ''}`}>
                    {(r.ctr * 100).toFixed(1)}%
                  </td>
                  <td className="p-3 text-right font-mono">{r.position?.toFixed(1)}</td>
                  {showPriorityScore && <td className="p-3 text-right font-mono font-bold text-primary">+{Math.round(r.priority_score)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


function GoldmineCard({ rows }) {
  if (!rows?.length) {
    return (
      <Card className="border border-border">
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          Ingen gullklumper denne runden.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border border-border">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          💎 Søkeord-gullklumper
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Søkeord hvor du har høy synlighet men lav CTR. Vurder å lage en <strong>dedikert landingsside</strong> for hvert av disse.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="text-left p-3 font-semibold">Søkeord</th>
                <th className="text-right p-3 font-semibold">Visn.</th>
                <th className="text-right p-3 font-semibold">Klikk nå</th>
                <th className="text-right p-3 font-semibold">Pos.</th>
                <th className="text-right p-3 font-semibold">CTR</th>
                <th className="text-right p-3 font-semibold">Potensial (5% CTR)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border hover:bg-muted/20">
                  <td className="p-3 font-medium">
                    {r.query}
                    <p className="text-xs text-muted-foreground italic mt-1">Lag dedikert landingsside for dette søket</p>
                  </td>
                  <td className="p-3 text-right font-mono">{r.impressions?.toLocaleString('nb-NO')}</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{r.clicks}</td>
                  <td className="p-3 text-right font-mono">{r.position?.toFixed(1)}</td>
                  <td className="p-3 text-right font-mono text-destructive">{(r.ctr * 100).toFixed(1)}%</td>
                  <td className="p-3 text-right font-mono font-bold text-primary">+{r.potential_clicks} klikk/mnd</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
